import { CompareProduct } from '../types';

export function renderApproximatePriceRow(products: CompareProduct[], maxCompare: number) {
  // Parse a price string respecting market-specific separators (thousands vs decimal)
  const parsePrice = (raw: string) => {
    const numeric = (raw || '').replace(/[^\d\s.,\u00A0\u202F-]/g, '').trim();
    if (!numeric) return NaN;

    const cleaned = numeric.replace(/[\s\u00A0\u202F]/g, '');
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    const lastSep = Math.max(lastDot, lastComma);

    if (lastSep === -1) return Number(cleaned);

    const trailing = cleaned.slice(lastSep + 1);
    // If exactly 2 digits after separator, it's decimal; otherwise thousands
    if (trailing.length === 2) {
      const sep = cleaned[lastSep];
      return Number(cleaned.replace(sep === ',' ? /,/g : /\./g, '.'));
    }

    // It's a thousands separator, remove it
    const intPart = cleaned.replace(/[.,]/g, '');
    return Number(intPart);
  };

  const cells = products
    .map((product) => {
      const approxRaw = product.newApproximatePrice || '';
      const priceRaw = product.price || '';

      const approx = parsePrice(approxRaw);
      const price = parsePrice(priceRaw);

      let saveHtml = '';

      const hasValidNumbers = !Number.isNaN(approx) && !Number.isNaN(price) && approx > price;

      if (hasValidNumbers) {
        const saved = approx - price;
        const percent = Math.round((saved / approx) * 100);
        const currency = approxRaw.replace(/[\d\s.,]/g, '').trim();

        const infoTooltip = window.compareTranslations?.infoTooltip;
        const saveText = window.compareTranslations?.save;

        // Format saved amount using the same separators as approxRaw
        const formatLikeRaw = (raw: string, value: number) => {
          const numeric = (raw || '').replace(/[^\d\s.,\u00A0\u202F-]/g, '').trim();
          if (!numeric) return value.toString();

          const cleaned = numeric.replace(/[\s\u00A0\u202F]/g, '');
          const lastDot = cleaned.lastIndexOf('.');
          const lastComma = cleaned.lastIndexOf(',');
          const lastSep = Math.max(lastDot, lastComma);

          if (lastSep === -1) return value.toFixed(0);

          const trailing = cleaned.slice(lastSep + 1);
          let thousandSep = '';
          let decimalSep = '';
          let fractionDigits = 0;

          // If exactly 2 digits after separator, it's decimal
          if (trailing.length === 2) {
            decimalSep = cleaned[lastSep];
            fractionDigits = 2;
            // Detect thousand separator from the raw string
            const thousandMatch = numeric.match(/[\s\u00A0\u202F.,](?=\d{3}(?:[\s\u00A0\u202F.,]|$))/);
            thousandSep = thousandMatch ? thousandMatch[0] : '';
          } else {
            // Otherwise it's a thousands separator
            thousandSep = cleaned[lastSep];
          }

          const abs = Math.abs(value);
          const intStr = Math.trunc(abs).toString();
          const grouped = thousandSep ? intStr.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep) : intStr;
          const fraction = fractionDigits ? (decimalSep || ',') + abs.toFixed(fractionDigits).split('.')[1] : '';

          return `${grouped}${fraction}`;
        };

        const formattedSaved = formatLikeRaw(approxRaw, saved);

        saveHtml = `
          <div class="label-xs m:label-s mt-1x py-1x relative">
            <span class="px-2x py-1x bg-brand-subtle rounded-s inline-flex w-fit items-center gap-2x">

              <span class="save-text">
                ${saveText} ${formattedSaved}${currency ? ` ${currency}` : ''} (-${percent}%)
              </span>

              <span class="save-info-trigger flex items-center cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7.5 7.5L7.52766 7.48617C7.90974 7.29513 8.33994 7.64023 8.23634 8.05465L7.76366 9.94535C7.66006 10.3598 8.09026 10.7049 8.47234 10.5138L8.5 10.5M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8ZM8 5.5H8.005V5.505H8V5.5Z"
                    stroke="#1A1A1A"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"/>
                </svg>

                <div class="flex gap-2x save-tooltip bg-brand-subtle hidden absolute left-0 bottom-full mt-2x bg-white shadow-m rounded-s p-3x text-content-secondary text-s leading-snug z-10">
                  <span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M7.5 7.5L7.52766 7.48617C7.90974 7.29513 8.33994 7.64023 8.23634 8.05465L7.76366 9.94535C7.66006 10.3598 8.09026 10.7049 8.47234 10.5138L8.5 10.5M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8ZM8 5.5H8.005V5.505H8V5.5Z"
                        stroke="#1A1A1A"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"/>
                    </svg>
                  </span>

                  <span>${infoTooltip || ''}</span>

                  <span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M0.599609 8.59961L8.59961 0.59961M0.599609 0.599609L8.59961 8.59961"
                        stroke="#1A1A1A"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>

              </span>

            </span>
          </div>
        `;
      }

      return `
        <div class="w-[144px] m:w-[200px] flex-1 shrink-0 p-2x">
          <div class="body-m m:body-l compare-value">
            ${approxRaw || '—'}
          </div>

          ${saveHtml}
        </div>
      `;
    })
    .join('');

  const emptyCells = Array.from({ length: maxCompare - products.length })
    .map(() => `<div class="w-[144px] m:w-[200px] flex-1 shrink-0 px-2x">—</div>`)
    .join('');

  const priceLabel = window.compareTranslations?.priceNew;

  return `
    <div class="compare-row mb-1x flex flex-col px-2x py-3x rounded-m border-secondary hover:bg-secondary">
      <span class="label-m mb-1x px-2x">${priceLabel}</span>
      <div class="flex gap-2x m:gap-6x items-start body-l">
        ${cells}
        ${emptyCells}
      </div>
    </div>
  `;
}
